import Konva from 'konva';
import { Vector2d } from 'konva/lib/types';
import { KONVA_JPG_QUALITY } from '../consts';
import { ETextAlignment, ETextAnchor } from '../enums';
import { IShape } from '../types';

export const getTextAlignFromAnchor = (anchor?: ETextAnchor): ETextAlignment => {
  if (anchor === ETextAnchor.START) return ETextAlignment.LEFT;
  if (anchor === ETextAnchor.END) return ETextAlignment.RIGHT;

  return ETextAlignment.CENTER;
};

export const stageToBlobAsync = async (
  stage: Konva.Stage,
  pixelRatio: number
): Promise<Blob | null> => {
  return new Promise((resolve) => {
    requestAnimationFrame(async () => {
      try {
        const pngUrl = stage.toDataURL({
          pixelRatio,
          mimeType: 'image/png',
          quality: 1,
        });

        const response = await fetch(pngUrl);
        const blob = await response.blob();

        resolve(blob);
      } catch (error) {
        console.error('Error during stage to blob conversion:', error);

        resolve(null);
      }
    });
  });
};

export const getCenteredOffset = (width: number, height: number): Konva.Vector2d => ({
  x: width / 2,
  y: height / 2,
});

export const degToRad = (angle: number): number => {
  return (angle / 180) * Math.PI;
};

export const getCenter = (shape: IShape): Vector2d => {
  const angleRad = degToRad(shape.rotation || 0);
  return {
    x: shape.x + (shape.width / 2) * Math.cos(angleRad) + (shape.height / 2) * Math.sin(-angleRad),
    y: shape.y + (shape.height / 2) * Math.cos(angleRad) + (shape.width / 2) * Math.sin(angleRad),
  };
};

interface IExistingShape extends IShape {
  rotation: number;
}

export const setRotationAroundPoint = (
  shape: IShape,
  absoluteDeg: number,
  point: Vector2d
): IExistingShape => {
  const currentRotation = shape.rotation || 0;
  const deltaDeg = absoluteDeg - currentRotation;
  const angleRad = degToRad(deltaDeg);
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);

  const newX = Math.round(point.x + (shape.x - point.x) * cos - (shape.y - point.y) * sin);
  const newY = Math.round(point.y + (shape.x - point.x) * sin + (shape.y - point.y) * cos);

  return {
    ...shape,
    rotation: Math.round(absoluteDeg),
    x: newX,
    y: newY,
  };
};

export const setRotationAroundCenter = (shape: IShape, absoluteDeg: number): IExistingShape => {
  const center = getCenter(shape);

  return setRotationAroundPoint(shape, absoluteDeg, center);
};

export const getRotationValuesAroundCenter = (
  shape: IShape,
  absoluteDeg: number
): IExistingShape => {
  const center = getCenter(shape);

  return setRotationAroundPoint(shape, absoluteDeg, center);
};
